/**
 * Write: the brief goes in here and the rules run backwards over it.
 *
 * A chord is typed the way the book names one — a bass note with an octave, then the rest of
 * the chord as bare letters. The roster is picked from the seats the generator actually knows;
 * anything it cannot seat is not offered, because a silent failure at orchestrate time would
 * be a worse answer than a shorter list.
 *
 * The board is laid out as the other tool board is: the roster, the strength table and the
 * verb on one strip at the top, the thing being written in the middle, and the sentence that
 * explains the strength table along the bottom. The progression is what changes from run to
 * run, so it is what the body is for; everything else is a setting, and settings belong on a
 * toolbar where they can be seen and changed without scrolling past the work.
 *
 * The chords sit in a column of their own width rather than across the whole window. A chord
 * is six characters and a row that stretched to a wide screen would be six characters of text
 * in a field the length of an arm.
 */

import { useNavigate } from "react-router-dom";
import { useAsync } from "../api/useAsync";
import { api } from "../api/client";
import { Divider, LABEL, Run, STRIP, Trouble } from "../components/Chrome";
import InstrumentPicker, { fromSeats } from "../components/InstrumentPicker";
import { useBrief } from "../state/brief";

/** The strength tables the book prints, and what picking one does. */
const REGIMES = [
  {
    name: "forte" as const,
    says: "Forte: the book's loud table, where a trumpet counts for more than a horn.",
  },
  {
    name: "piano" as const,
    says: "Piano: the quiet table, where the same seats weigh differently and the balance rule moves with them.",
  },
];

export default function WritePage(): JSX.Element {
  const navigate = useNavigate();
  const brief = useBrief();
  const seats = useAsync("seats", () => api.seats());

  const tally = new Map<string, number>();
  for (const one of brief.roster) tally.set(one, (tally.get(one) ?? 0) + 1);
  const labelFor = (instrument: string): string =>
    seats.data?.seats.find((one) => one.instrument === instrument)?.label ?? instrument;

  const setChord = (index: number, text: string): void => {
    const next = [...brief.chords];
    next[index] = text;
    brief.setChords(next);
  };

  const run = (): void => {
    void brief.orchestrate().then((payload) => {
      if (payload) navigate("/score");
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={STRIP}>
        <span className={LABEL}>Roster</span>
        <div className="flex flex-wrap items-center gap-1">
          {[...tally].map(([instrument, many]) => (
            <span
              key={instrument}
              className="inline-flex h-7 items-center gap-1 rounded bg-shell-key pl-2 pr-1 text-[11px] text-shell-lit"
            >
              {labelFor(instrument)}
              {many > 1 ? <span className="font-mono text-shell-dim">×{many}</span> : null}
              <button
                type="button"
                aria-label={`Remove one ${labelFor(instrument)}`}
                onClick={() => {
                  const at = brief.roster.lastIndexOf(instrument);
                  brief.setRoster([...brief.roster.slice(0, at), ...brief.roster.slice(at + 1)]);
                }}
                className="h-5 w-5 rounded text-shell-dim hover:bg-shell-hot hover:text-shell-lit"
              >
                ×
              </button>
            </span>
          ))}
          {seats.data === null ? null : (
            <InstrumentPicker
              look="shell"
              label="Add a player"
              placeholder="+ Player"
              value={null}
              options={fromSeats(seats.data.seats.filter((seat) => seat.voiced))}
              onPick={(instrument) => brief.setRoster([...brief.roster, instrument])}
              title="The balance rule quantifies one group at a time — a mixed roster is measured, not judged."
              className="w-32"
            />
          )}
        </div>

        <Divider />

        <span className={LABEL}>Strength</span>
        <div className="flex items-center rounded bg-shell-key p-0.5">
          {REGIMES.map((one) => (
            <button
              key={one.name}
              type="button"
              onClick={() => brief.setRegime(one.name)}
              aria-pressed={brief.regime === one.name}
              className={`h-6 rounded px-2.5 text-[11px] capitalize ${
                brief.regime === one.name
                  ? "bg-shell-hot text-shell-lit"
                  : "text-shell-dim hover:text-shell-text"
              }`}
            >
              {one.name}
            </button>
          ))}
        </div>

        <Run
          label={brief.running ? "Orchestrating…" : "Orchestrate"}
          disabled={brief.running || brief.roster.length === 0}
          onRun={run}
        />
      </div>

      {seats.error === null ? null : (
        <Trouble>
          The pipeline did not answer when asked which seats it knows: {seats.error}
        </Trouble>
      )}
      {brief.error === null ? null : <Trouble>{brief.error}</Trouble>}

      <div className="min-h-0 flex-1 overflow-y-auto bg-canvas/40 px-3 py-4">
        <div className="mx-auto w-full max-w-md space-y-2">
          <div className="flex items-baseline justify-between px-0.5">
            <span className={LABEL}>Progression</span>
            <span className="font-mono text-[10px] text-shell-dim">
              {brief.chords.length} chord{brief.chords.length === 1 ? "" : "s"}
            </span>
          </div>

          <ol className="space-y-1">
            {brief.chords.map((chord, index) => (
              <li key={index} className="flex items-center gap-1.5">
                <span className="w-5 shrink-0 text-right font-mono text-[10px] text-shell-dim">
                  {index + 1}
                </span>
                <input
                  value={chord}
                  onChange={(event) => setChord(index, event.target.value)}
                  spellCheck={false}
                  aria-label={`Chord ${index + 1}`}
                  title="The first note carries an octave and is taken as the bass; the rest are bare letters and the generator places them."
                  className="h-8 flex-1 rounded bg-shell-key px-2 font-mono text-[13px] text-shell-lit outline-none focus:ring-1 focus:ring-shell-live"
                />
                <button
                  type="button"
                  onClick={() =>
                    brief.setChords([
                      ...brief.chords.slice(0, index),
                      ...brief.chords.slice(index + 1),
                    ])
                  }
                  disabled={brief.chords.length === 1}
                  aria-label={`Remove chord ${index + 1}`}
                  className="h-8 w-8 shrink-0 rounded text-shell-dim hover:bg-shell-hot hover:text-shell-lit disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  ×
                </button>
              </li>
            ))}
          </ol>

          <button
            type="button"
            onClick={() => brief.setChords([...brief.chords, "C3 E G"])}
            className="ml-[26px] h-8 rounded border border-dashed border-shell-seam px-3 text-[11px] text-shell-dim hover:border-shell-hot hover:text-shell-text"
          >
            + Add a chord
          </button>
        </div>
      </div>

      <p className="shrink-0 border-t border-shell-edge bg-shell-bar px-2.5 py-1.5 text-[11px] leading-relaxed text-shell-dim">
        {REGIMES.find((one) => one.name === brief.regime)?.says}
      </p>
    </div>
  );
}
